import React, { useEffect, useMemo, useState } from "react";
import { Mail, Paperclip, Plus, Search } from "lucide-react";
import type {
  FacultyCommunicationDraft,
  FacultyMember,
} from "../../types/faculty";

interface FacultyCommunicationsTabProps {
  faculty: FacultyMember;
  initialSelectedCommunicationId?: string | null;
  onSelectedCommunicationChange?: (id: string | null) => void;
  onComposeCommunication?: (
    draft: FacultyCommunicationDraft,
  ) => Promise<void> | void;
}

const previewText = (value: string) => value.slice(0, 120);

export function FacultyCommunicationsTab({
  faculty,
  initialSelectedCommunicationId = null,
  onSelectedCommunicationChange,
  onComposeCommunication,
}: FacultyCommunicationsTabProps) {
  const [selectedCommId, setSelectedCommId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");

  useEffect(() => {
    setSelectedCommId(initialSelectedCommunicationId);
  }, [initialSelectedCommunicationId]);

  const filteredComms = useMemo(
    () =>
      faculty.communications.filter(
        (communication) =>
          communication.subject
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          communication.preview
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
      ),
    [faculty.communications, searchQuery],
  );

  const selectedComm =
    faculty.communications.find(
      (communication) => communication.id === selectedCommId,
    ) ?? null;

  const handleCompose = async () => {
    if (!composeTo.trim() || !composeBody.trim()) return;
    await onComposeCommunication?.({
      to: composeTo.trim(),
      subject: composeSubject.trim(),
      body: composeBody.trim(),
    });
    setShowComposeModal(false);
    setComposeTo("");
    setComposeSubject("");
    setComposeBody("");
  };

  return (
    <>
      <div
        data-testid="faculty-live-communications"
        className="flex h-[600px] flex-col overflow-hidden rounded-xl border border-border bg-white shadow-sm"
      >
        <div className="flex items-center justify-between border-b border-border bg-white p-4">
          <div className="relative w-64">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-text-tertiary" />
            </div>
            <input
              type="text"
              className="block w-full rounded-md border border-border bg-white py-1.5 pl-9 pr-3 text-sm text-iron-grey placeholder-text-tertiary leading-5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Search communications..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
          <button
            type="button"
            data-testid="compose-btn"
            onClick={() => setShowComposeModal(true)}
            className="inline-flex items-center rounded-md border border-transparent bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Compose
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div
            data-testid="communications-list"
            className="w-1/3 overflow-y-auto border-r border-border bg-white"
          >
            {filteredComms.length === 0 ? (
              <div className="p-6 text-center text-sm text-text-secondary">
                Try a different search to surface communications.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredComms.map((communication) => (
                  <li
                    key={communication.id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedCommId === communication.id}
                    className={`p-4 transition-colors hover:bg-muted/50 ${
                      selectedCommId === communication.id
                        ? "border-l-2 border-l-primary bg-muted/30"
                        : "border-l-2 border-l-transparent"
                    }`}
                    onClick={() => {
                      setSelectedCommId(communication.id);
                      onSelectedCommunicationChange?.(communication.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedCommId(communication.id);
                        onSelectedCommunicationChange?.(communication.id);
                      }
                    }}
                  >
                    <div className="mb-1 flex items-baseline justify-between">
                      <span className="truncate pr-2 text-sm font-medium text-iron-grey">
                        {communication.sender}
                      </span>
                      <span className="whitespace-nowrap text-xs text-text-tertiary">
                        {communication.date}
                      </span>
                    </div>
                    <p className="mb-1 truncate text-sm font-medium text-iron-grey">
                      {communication.subject}
                    </p>
                    <p className="line-clamp-2 text-xs text-text-secondary">
                      {communication.preview ||
                        previewText(communication.fullContent)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="w-2/3 overflow-y-auto bg-white">
            {selectedComm ? (
              <div data-testid="comm-detail" className="p-6">
                <h2 className="mb-4 font-heading text-xl font-semibold text-iron-grey">
                  {selectedComm.subject}
                </h2>

                <div className="mb-6 flex items-start justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted font-medium text-text-secondary">
                      {selectedComm.sender.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-iron-grey">
                        {selectedComm.sender}
                      </p>
                      <p className="text-xs text-text-secondary">
                        To:{" "}
                        {selectedComm.recipients.join(", ") ||
                          faculty.workEmail}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm text-text-secondary">
                    {selectedComm.date}
                  </span>
                </div>

                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-iron-grey">
                  {selectedComm.fullContent}
                </div>

                {selectedComm.attachments?.length ? (
                  <div className="mt-8 border-t border-border pt-4">
                    <h4 className="mb-3 flex items-center text-sm font-medium text-iron-grey">
                      <Paperclip className="mr-2 h-4 w-4 text-text-secondary" />
                      Attachments ({selectedComm.attachments.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedComm.attachments.map((attachment) => (
                        <div
                          key={attachment.resourceId}
                          className="flex items-center rounded-md border border-border bg-muted px-3 py-2 text-sm text-iron-grey"
                        >
                          {attachment.fileName}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div
                data-testid="comm-empty"
                className="flex h-full flex-col items-center justify-center p-6 text-text-secondary"
              >
                <Mail className="mb-4 h-12 w-12 text-text-tertiary" />
                <p className="mb-1 text-lg font-medium text-iron-grey">
                  Select a message
                </p>
                <p className="text-sm">
                  Choose a communication from the list to view its details.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showComposeModal ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-iron-grey/50 px-4">
          <div
            data-testid="compose-modal"
            className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl"
          >
            <h3 className="mb-4 font-heading text-xl font-semibold text-iron-grey">
              Compose communication
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-iron-grey">
                  To
                </label>
                <input
                  data-testid="compose-to"
                  value={composeTo}
                  onChange={(event) => setComposeTo(event.target.value)}
                  className="w-full rounded-md border border-border px-3 py-2 text-sm text-iron-grey focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-iron-grey">
                  Subject
                </label>
                <input
                  data-testid="compose-subject"
                  value={composeSubject}
                  onChange={(event) => setComposeSubject(event.target.value)}
                  className="w-full rounded-md border border-border px-3 py-2 text-sm text-iron-grey focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-iron-grey">
                  Message
                </label>
                <textarea
                  data-testid="compose-body"
                  value={composeBody}
                  onChange={(event) => setComposeBody(event.target.value)}
                  className="min-h-[12rem] w-full rounded-md border border-border px-3 py-2 text-sm text-iron-grey focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowComposeModal(false)}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleCompose()}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
